import type {
  JSONContent,
  MarkdownParseHelpers,
  MarkdownRendererHelpers,
  MarkdownToken,
  MarkdownTokenizer,
} from "@tiptap/core"
import * as z from "zod"
import { MARK_COLORS, type MarkColorName } from "./colors"
import { shouldIncludeAttributes } from "./markdown-options"
import {
  formatXmlAttributes,
  parseXmlAttributes,
  XML_ATTRIBUTES_SOURCE,
} from "./markdown-xml-attributes"

const HtmlMarkAttrsSchema = z.object({ color: z.string().optional() })

function escapeRegex(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
}

/**
 * Reads HTML-like `<tag>…</tag>` or `<tag color="name">…</tag>` as a mark token, parsing the
 * inside as Markdown.
 *
 * We use our own tokenizer instead of deferring to Tiptap's HTML parser,
 * to allow custom HTML attributes (not present in the Tiptap HTML) and markdown
 * nested inside a tag.
 */
export function htmlMarkTokenizer(
  name: string,
  tag: string,
  { requireColor = false } = {},
): MarkdownTokenizer {
  const escaped = escapeRegex(tag)
  const opening = new RegExp(`^<${escaped}(${XML_ATTRIBUTES_SOURCE})\\s*>`, "i")
  const nestedTag = new RegExp(`<(/?)${escaped}\\b[^>]*>`, "gi")

  return {
    name,
    level: "inline",
    start: (src) => src.search(new RegExp(`<${escaped}[\\s>]`, "i")),
    tokenize(src, _tokens, helpers) {
      const open = opening.exec(src)
      if (!open) return
      const attrs = parseXmlAttributes(open[1], HtmlMarkAttrsSchema)
      if (!attrs || (requireColor && !attrs.color)) return
      const color = attrs.color

      // Same-name tags can nest (<u><u>…</u></u>), so the first close is not
      // necessarily this one's.
      let depth = 1
      nestedTag.lastIndex = open[0].length
      for (let match = nestedTag.exec(src); match; match = nestedTag.exec(src)) {
        depth += match[1] ? -1 : 1
        if (depth > 0) continue

        const text = src.slice(open[0].length, match.index)
        return {
          type: name,
          raw: src.slice(0, match.index + match[0].length),
          text,
          color,
          tokens: helpers.inlineTokens(text),
        }
      }
      return
    },
  }
}

/** A palette name becomes its color; anything else is kept as the literal color. */
export function parseHtmlMark(
  token: MarkdownToken,
  helpers: MarkdownParseHelpers,
  mark: string,
  colors?: Record<MarkColorName, string>,
  defaultColor?: string,
) {
  const value = typeof token.color === "string" && token.color ? token.color : undefined
  const name = value?.toLowerCase() as MarkColorName | undefined
  const color =
    name && colors && MARK_COLORS.includes(name) ? colors[name] : (value ?? defaultColor)
  return helpers.applyMark(
    mark,
    helpers.parseInline(token.tokens ?? []),
    color ? { color } : undefined,
  )
}

export function renderHtmlMark(
  node: JSONContent,
  helpers: MarkdownRendererHelpers,
  tag: string,
  colors?: Record<MarkColorName, string>,
): string {
  const children = helpers.renderChildren(node.content || [])
  const color: unknown = shouldIncludeAttributes() ? node.attrs?.color : undefined
  const name =
    colors && typeof color === "string" && color
      ? (MARK_COLORS.find((candidate) => colors[candidate] === color) ?? color)
      : undefined
  return `<${tag}${formatXmlAttributes({ color: name })}>${children}</${tag}>`
}
